import React from "react";
import { useLocation, Link } from "react-router-dom";
import BackArrow from "../assets/images/back-arrow.svg";
import CameraIcon from "../assets/icons/camera.svg";
import SaveIcon from "../assets/icons/save.svg";
import DetectedIcon from "../assets/icons/detected.svg";
import Recomand from "../assets/icons/Recommendations.svg";

import DetectedCard from "../components/DetectedCard";
import AnalysisResult from "../components/AnalysisCard";
import PoreAnalysisZone from "../components/PoreAnalysisZone";

import DarkSpots from "../assets/icons/dark-spot2.svg";
import Wrinkles from "../assets/icons/dark-spot.svg";
import MoistureIcon from "../assets/icons/moisturizing.svg";
import RednessIcon from "../assets/icons/red-blood-cells.svg";
import SkinFirmnessIcon from "../assets/icons/skin-firming.svg";
import LowerEyelidIcon from "../assets/icons/lower-eyelid.svg";
import UpperEyelidIcon from "../assets/icons/upper-eyelid.svg";
import SkinRadianceIcon from "../assets/icons/Skin-Radiance.svg";

const iconMap = {
  spots: DarkSpots,
  wrinkles: Wrinkles,
  dark_circles: DarkSpots,
  eye_bags: Wrinkles,
  moisture: MoistureIcon,
  oiliness: MoistureIcon,
  redness: RednessIcon,
  acne: DarkSpots,
  pores_full_face: DarkSpots,
  firmness: SkinFirmnessIcon,
  droopy_lower_eyelid: LowerEyelidIcon,
  droopy_upper_eyelid: UpperEyelidIcon,
  radiance: SkinRadianceIcon,
  tear_trough: DarkSpots,
};

const DermaResult = () => {
  const { state } = useLocation();
  const result = state.analysis;
  const capturedImage = state.image;

  return (
    <section className="pt-2 flex flex-col">
      <div className="flex items-center px-6 mb-6 tab-screen lg:mb-8 md:mb-8">
        <Link
          to="https://gitex-demo.careplix.com/UnityHealthExperienceCenter"
          className="w-9 h-9 bg-[#2D2D2D] rounded-full flex justify-center items-center"
        >
          <img src={BackArrow} alt="Back" />
        </Link>
        <h2 className="text-xl lg:text-2xl md:text-2xl font-medium text-white ml-6">
          Derma <span className="text-secondary">Ai</span>
        </h2>
        <div></div>
      </div>

      <h3 className="text-base font-semibold text-white mb-2 text-center">
        Assessment Result
      </h3>
      <p className="text-xs text-white text-center mb-6">
        {`AI analysis for your "${
          state.selectedArea
        }" — ${new Date().toLocaleDateString()}`}
      </p>

      {/* Clinical Assessment */}
      <div className="px-6 mb-6 relative z-50 tab-screen">
        <div className="bg-[#1D1D1D]/70 rounded-[20px] p-6 text-center">
          {capturedImage ? (
            <img
              src={capturedImage}
              className="w-[300px] h-[225px] object-cover rounded-2xl mx-auto"
              alt="Captured Skin"
            />
          ) : (
            <span className="text-white text-sm">No image available</span>
          )}
          <p className="text-white text-xl mt-5">
            Overall Skin Health:{" "}
            <span
              className={`text-xl font-bold ${
                {
                  excellent: "text-green-700",
                  good: "text-green-500",
                  fair: "text-yellow-500",
                  needs_attention: "text-red-500",
                }[result.overall_skin_health] || "text-gray-500"
              }`}
            >
              {result.overall_skin_health === "needs_attention"
                ? "Need Attention"
                : result.overall_skin_health.charAt(0).toUpperCase() +
                  result.overall_skin_health.slice(1)}
            </span>
          </p>
          <p className="text-white/80 text-xs mt-2">
            {result.clinical_explanation}
          </p>
          {result.should_see_dermatologist && (
            <p className="text-red-400 mt-2 font-semibold">
              We recommend seeing a dermatologist.
            </p>
          )}
        </div>
      </div>

      {/* Detected Conditions */}
      <div className="bg-white pb-20 pt-6 mt-8">
        <div className="tab-screen">
          <div className="px-6 w-full">
            <div className="bg-[#F6F6F6] p-6 rounded-t-xl lg:rounded-xl md:rounded-xl">
              <div className="flex items-center mb-4">
                <img src={DetectedIcon} className="mr-2" alt="" />
                <h4 className="text-base font-medium text-black">
                  Detected Conditions
                </h4>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {result?.detected_conditions.map((c, i) => (
                  <DetectedCard
                    key={i}
                    title={c.condition}
                    severity={c.severity}
                    confidence={`${Math.round(c.confidence * 100)}% confidence`}
                    description={c.description}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Facial Analysis */}
          {result?.facial_analysis && (
            <div className="px-6 mt-4 w-full">
              <div className="flex items-center mb-4">
                <div className="w-9 h-9 bg-[#EDF4FB] rounded-full flex items-center justify-center mr-3">
                  <img src={iconMap.spots} alt="" className="w-6 h-6" />
                </div>
                <h4 className="text-base font-medium text-black">
                  Comprehensive Facial Analysis
                </h4>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(result.facial_analysis).map(
                  ([key, val], idx) => {
                    const icon = iconMap[key] || DarkSpots;
                    const label = key
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase());
                    const severityOrLevel =
                      val.severity || val.level || val.visibility || "N/A";
                    const confidence = val.confidence
                      ? `${Math.round(val.confidence * 100)}%`
                      : "";
                    return (
                      <AnalysisResult
                        key={idx}
                        icon={icon}
                        title={label}
                        severity={severityOrLevel}
                        confidence={confidence}
                        description={val.description}
                      />
                    );
                  }
                )}
              </div>

              <PoreAnalysisZone
                zones={[
                  {
                    label: "T Zone",
                    severity:
                      result.facial_analysis.pores_detailed_zones.t_zone,
                    confidence: 0,
                  },
                  {
                    label: "Cheek Area",
                    severity:
                      result.facial_analysis.pores_detailed_zones.cheek_area,
                    confidence: 0,
                  },
                  {
                    label: "Chin Area",
                    severity:
                      result.facial_analysis.pores_detailed_zones.chin_area,
                    confidence: 0,
                  },
                ]}
              />
            </div>
          )}

          {/* Recommendations */}
          <div className="px-6 mt-3">
            <div className="flex items-center mb-4">
              <img src={Recomand} className="mr-2" alt="" />
              <h4 className="text-base font-medium text-black">
                Care Recommendations
              </h4>
            </div>
            {Object.entries(result.recommendations).map(
              ([title, content], i) => (
                <div
                  key={i}
                  className="bg-[#2E3A59] text-white text-xs rounded-[10px] px-4 py-3 mb-2 flex"
                >
                  <span className="mr-2">{i + 1}.</span>
                  <span>{content}</span>
                </div>
              )
            )}

            {/* Navigation Buttons */}
            <div className="mt-4 grid grid-cols-2 gap-3">
              <Link
                to="/skin-analyzer"
                className="bg-black text-white py-3 rounded-full flex items-center justify-center gap-2 text-sm shadow"
              >
                <img src={CameraIcon} alt="" /> New Analysis
              </Link>
              <button
                onClick={() => alert("Saved!")}
                className="border border-gray-400 text-gray-900 py-3 rounded-full flex items-center justify-center gap-2 text-sm"
              >
                <img src={SaveIcon} alt="" /> Save Results
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DermaResult;
